export const MAX_VERSIONS = 3;
export const DEFAULT_SCALE_PERCENT = 20;

export type ScaleDirection = 'FEWER' | 'MORE';
export type VersionSource = 'COPY' | 'COPY_SCALED' | 'TEMPLATE';

export interface VersionSummary {
	readonly minutes: number | null;
	readonly valueOfService: number | null;
	readonly revenue: number | null;
	readonly cost: number | null;
	readonly profit: number | null;
	readonly margin: number | null;
	readonly expenseRevenue: number | null;
	readonly expenseCost: number | null;
}

export interface VersionPreview {
	revenue: number;
	minutes: number;
	margin: number | null;
}

const FEWER_MAX = 95;
const MORE_MAX = 200;

export const scaleRange = (direction: ScaleDirection) => ({min: 1, max: direction === 'FEWER' ? FEWER_MAX : MORE_MAX});

export const isScaleInRange = (direction: ScaleDirection, percent: number | null | undefined): boolean => {
	if (percent == null || !Number.isInteger(percent)) {
		return false;
	}
	const {min, max} = scaleRange(direction);
	return percent >= min && percent <= max;
};

const n = (value: number | null) => value ?? 0;

export const copyPreview = (standard: VersionSummary): VersionPreview => ({
	revenue: n(standard.revenue),
	minutes: n(standard.minutes),
	margin: standard.margin,
});

export const formatMoney = (amount: number, currencySymbol: string): string =>
	`${amount < 0 ? '-' : ''}${currencySymbol}${Math.abs(Math.round(amount)).toLocaleString('en-US')}`;

/** Hours to at most two decimals, so a scaled estimate such as 2h 24m reads 2.4 rather than rounding back to 2. */
export const formatHours = (minutes: number): string =>
	(Math.round((minutes / 60) * 100) / 100).toLocaleString('en-US', {maximumFractionDigits: 2});

export const formatPercent = (fraction: number): string => `${Math.round(fraction * 100)}`;

export const signedPercent = (direction: ScaleDirection, percent: number): string =>
	`${direction === 'FEWER' ? '−' : '+'}${percent}%`;

export type CompareSummary = Pick<VersionSummary, 'revenue' | 'cost' | 'profit' | 'margin' | 'minutes'>;

/** A version's figures as the compare table shows them; differences are worked out from these. */
export interface CompareFigures {
	revenue: number;
	cost: number | null;
	profit: number | null;
	margin: number | null;
	hours: number;
}

export type Metric = 'revenue' | 'cost' | 'profit' | 'margin' | 'hours';

const cents = (value: number) => Math.round(value * 100) / 100;

/** A missing margin next to a known cost means no revenue, which reads 0%, as the banner shows it. */
export const compareFigures = (s: CompareSummary): CompareFigures => ({
	revenue: cents(s.revenue ?? 0),
	cost: s.cost == null ? null : cents(s.cost),
	profit: s.profit == null ? null : cents(s.profit),
	margin: s.margin != null ? Math.round(s.margin * 100) : s.cost == null ? null : 0,
	hours: Math.round((s.minutes ?? 0) / 60),
});

/** The version's figure minus Standard's, or null when either is hidden. Money stays in cents to avoid float noise. */
export const delta = (metric: Metric, version: CompareFigures, standard: CompareFigures): number | null => {
	const a = version[metric];
	const b = standard[metric];
	if (a == null || b == null) {
		return null;
	}
	return metric === 'margin' || metric === 'hours' ? a - b : cents(a - b);
};

/** Green when better, red when worse; lower is better for cost, and hours are neither. */
export const deltaTone = (metric: Metric, d: number): 'good' | 'bad' | 'neutral' => {
	if (metric === 'hours') {
		return 'neutral';
	}
	const better = metric === 'cost' ? d < 0 : d > 0;
	return better ? 'good' : 'bad';
};

export const formatMoneyCents = (amount: number, currencySymbol: string): string =>
	`${amount < 0 ? '-' : ''}${currencySymbol}${Math.abs(amount).toLocaleString('en-US', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`;

export const targetProgress = (target: number, compared: number) => ({
	percent: target > 0 ? Math.round(Math.min(100, (compared / target) * 100) * 100) / 100 : 0,
	difference: target - compared,
});
